import React, { useEffect, useRef, useState } from "react";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { mapStyles } from "../../../../../utils/map-styles.js";
import MapControls from "../../../../../components/map-report/map-buttons-control.jsx";
import { useSpiderfy } from "../../../../../components/map-report/spiderfy-marker.jsx";
import { useInfoWindow } from "../../../../../components/map-report/info-window.jsx";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";

const API_URL = import.meta.env.VITE_API_URL;

const MapContent = ({ coordinates = [], layers = [], showLayer, mapMode }) => {
    const map = useMap();
    const mapsLib = useMapsLibrary("maps");

    const routeRef = useRef(null);
    const heatmapRef = useRef(null);

    const markersRef = useRef([]);
    const markersMapRef = useRef(new Map());

    const polygonsRef = useRef([]);
    const originalPosRef = useRef(new Map());

    const [mapState, setMapState] = useState({
        neighborhoodStreets: false,
        pointsOfInterest: false
    });

    const {init: initInfoWindow, openInfoWindow, close: closeInfoWindow} = useInfoWindow(map, mapsLib);
    const {spiderfy, unspiderfy, setHoverLine, resetHoverLine, spiderfiedRef} = useSpiderfy(map);

    useEffect(() => {
        if (!map || !mapsLib) return;

        routeRef.current ??= new mapsLib.Polyline({
            geodesic: true,
            strokeColor: "#1a73e8",
            strokeWeight: 2,
        });

        initInfoWindow();
    }, [map, mapsLib]);

    useEffect(() => {
        if (!map) return;

        map.setOptions({
            styles: mapStyles({
                neighborhoodStreets: mapState.neighborhoodStreets,
                pointsOfInterest: mapState.pointsOfInterest
            })
        });
    }, [mapState, map]);

    const buildData = () => {
        const heatmap = [];
        const route = [];
        const details = [];

        coordinates.forEach((c, index) => {
            const lat = parseFloat(c.latitude);
            const lng = parseFloat(c.longitude);
            if (isNaN(lat) || isNaN(lng)) return;

            const latLng = new google.maps.LatLng(lat, lng);

            heatmap.push(latLng);
            route.push({ lat, lng });
            details.push({ ...c, latLng, id: index });
        });

        return { heatmap, route, details };
    };

    useEffect(() => {
        if (!map) return;

        if (!heatmapRef.current) {
            heatmapRef.current = new GoogleMapsOverlay({});
            heatmapRef.current.setMap(map);
        }
    }, [map]);

    useEffect(() => {
        if (!map || !mapsLib) return;

        routeRef.current?.setMap(null);

        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];
        markersMapRef.current.clear();

        const { heatmap, route, details } = buildData();

        const bounds = new google.maps.LatLngBounds();
        route.forEach(p => bounds.extend(p));

        const applyBounds = () => {
            if (!bounds.isEmpty()) map.fitBounds(bounds);
        };

        if (heatmapRef.current) {
            heatmapRef.current.setProps({ layers: [] });
        }

        if (mapMode === "heatmap-mode") {
            const heatmapData = heatmap.map(latLng => ({position: [latLng.lng(), latLng.lat()], weight: 1}));
            heatmapRef.current.setProps({
                layers: [
                    new HeatmapLayer({
                        id: "heatmap",
                        data: heatmapData,

                        getPosition: data => data.position,
                        getWeight: data => data.weight,

                        radiusPixels: 40,
                        intensity: 1,
                        threshold: 0.05
                    })
                ]
            });

            heatmapRef.current.setMap(map);
            applyBounds();
        }

        if (mapMode === "routes-mode") {
            routeRef.current.setOptions({ strokeOpacity: 1 });
            routeRef.current.setPath(route);
            routeRef.current.setMap(map);
            applyBounds();
        }

        if (mapMode === "detail-mode") {
            routeRef.current.setOptions({ strokeOpacity: 0.5 });
            routeRef.current.setPath(route);
            routeRef.current.setMap(map);

            const vehicleType = details[0]?.vehicle_type;

            details.forEach((point) => {
                const marker = new google.maps.Marker({
                    position: point.latLng,
                    map,
                    icon: {
                        url: `${API_URL}/public/assets/img/markers-gis/garrison/${vehicleType}/dark.svg`,
                        scaledSize: new google.maps.Size(35, 35)
                    }
                });

                marker.originalPosition = point.latLng;
                marker.pointData = point;

                originalPosRef.current.set(point.id, marker.getPosition());

                markersRef.current.push(marker);
                markersMapRef.current.set(marker, point);

                marker.addListener("click", () => {
                    const projection = map.getProjection();
                    if (!projection) return;

                    const group = markersRef.current.filter(m => {
                        const p1 = projection.fromLatLngToPoint(m.getPosition());
                        const p2 = projection.fromLatLngToPoint(marker.getPosition());

                        const scale = Math.pow(2, map.getZoom());
                        const dx = (p1.x - p2.x) * scale;
                        const dy = (p1.y - p2.y) * scale;

                        return Math.sqrt(dx * dx + dy * dy) < 17;
                    });

                    if (group.length > 1 && !spiderfiedRef.current) {
                        spiderfy(group);
                        return;
                    }

                    const original = marker.originalPosition;

                    openInfoWindow(marker, {
                        title: "Detalhamento",
                        fields: [
                            { value: `Data/hora: ${point.device_time}` },
                            { value: `Latitude: ${original.lat()} | Longitude: ${original.lng()}` }
                        ],
                        context: {
                            lat: original.lat(),
                            lng: original.lng()
                        }
                    });
                });

                marker.addListener("mouseover", () => setHoverLine(marker));
                marker.addListener("mouseout", () => resetHoverLine(marker));
            });

            applyBounds();
        }

        polygonsRef.current.forEach(p => p.setMap(null));
        polygonsRef.current = [];

        if (showLayer && layers?.length) {
            layers.forEach(layer => {
                try {
                    const raw = JSON.parse(layer.coordinates);

                    const coords = [];
                    for (let i = 0; i < raw.length; i += 2) {
                        coords.push({
                            lat: Number(raw[i]),
                            lng: Number(raw[i + 1]),
                        });
                    }

                    const polygon = new google.maps.Polygon({
                        paths: coords,
                        strokeColor: layer.color,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: layer.color,
                        fillOpacity: 0.35,
                    });

                    polygon.setMap(map);

                    polygon.addListener("click", () => {
                        closeInfoWindow();
                        if (spiderfiedRef.current) unspiderfy(markersRef, originalPosRef);
                    });

                    polygonsRef.current.push(polygon);

                } catch (e) {
                    console.error("Erro layer", e);
                }
            });
        }

    }, [coordinates, mapMode, showLayer, layers, map, mapsLib]);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener("click", () => {
            closeInfoWindow();

            if (spiderfiedRef.current) {
                unspiderfy(markersRef, originalPosRef);
            }
        });

        return () => listener.remove();
    }, [map]);

    return (
        <MapControls
            mapState={mapState}
            onTogglePOI={() => setMapState(p => ({ ...p, pointsOfInterest: !p.pointsOfInterest }))}
            onToggleNeighborhood={() => setMapState(p => ({ ...p, neighborhoodStreets: !p.neighborhoodStreets }))}
        />
    );
};

export default MapContent;